import React, { useEffect, useState, useContext } from "react";
import Input from "@material-ui/core/Input";
import {
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  TableSortLabel,
  useTheme,
  IconButton,
  TableContainer,
  Paper,
  TablePagination,
  TableFooter,
} from "@material-ui/core";
import OwnerHeader from "./Components/OwnerHeader";
import { useHistory, useParams } from "react-router-dom";
import GlobalContext from "../../Context/globalContext";
import { Skeleton } from "@material-ui/lab";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { Instance } from "../../Config";
import useSWR from "swr";
import { SHOW_SNACKBAR } from "../../Context/globalActions.types";
import SearchIcon from "@material-ui/icons/Search";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";

// Helper functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Styles
const useStyles = makeStyles((theme) => ({
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

// Table configuration
const headCells = [
  { id: "GROUP_CD", label: "Group Code" },
  { id: "USER_CD", label: "User Code" },
  { id: "USER_NAME", label: "User Name" },
  { id: "AREA", label: "Area" },
  { id: "CITY", label: "City" },
  { id: "PHONE1", label: "Phone" },
  { id: "CR_LIMIT", label: "Credit Limit" },
];

// CSV Export headers
const csvHeaders = [
  { label: "User Code", key: "USER_CD" },
  { label: "User Name", key: "USER_NAME" },
  { label: "Person Name", key: "PERSON_NM" },
  { label: "Group Code", key: "GROUP_CD" },
  { label: "Address 1", key: "ADD1" },
  { label: "Address 2", key: "ADD2" },
  { label: "Area", key: "AREA" },
  { label: "City", key: "CITY" },
  { label: "Pincode", key: "PINCODE" },
  { label: "Phone 1", key: "PHONE1" },
  { label: "Phone 2", key: "PHONE2" },
  { label: "Credit Limit", key: "CR_LIMIT" },
  { label: "Current Balance", key: "CL_BAL" },
];

// Enhanced Table Head Component
function EnhancedTableHead({ classes, order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className="sticky top-0 z-10">
      <TableRow className="grid grid-cols-7 pb-1 py-2 md:grid-cols-12 md:mx-3 md:bg-white bg-gray-200">
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            className={`font-bold border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-5 w-max bg-transparent ${
              headCell.id === "USER_NAME"
                ? "md:col-span-3 col-span-2"
                : "md:col-span-1 col-span-1"
            }`}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Table Pagination Actions Component
function TablePaginationActions({ page, onPageChange, hasNextPage }) {
  const classes = useStyles1();
  const theme = useTheme();

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={!hasNextPage}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
    </div>
  );
}

// Table Row Component
const UserTableRow = ({ user }) => (
  <TableRow
    key={user.USER_CD}
    className="grid grid-cols-7 py-1 items-center md:grid-cols-12 md:mx-3 border-t-[1px] hover:font-semibold hover:bg-gray-50"
  >
    <TableCell className="border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 col-span-1">
      {user.GROUP_CD || "-"}
    </TableCell>
    <TableCell className="border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 col-span-1">
      {user.USER_CD}
    </TableCell>
    <TableCell className="border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 md:col-span-3 col-span-2">
      <span className="line-clamp-2">{user.USER_NAME}</span>
    </TableCell>
    <TableCell className="border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 col-span-1">
      {user.AREA || "-"}
    </TableCell>
    <TableCell className="border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 col-span-1">
      {user.CITY || "-"}
    </TableCell>
    <TableCell className="border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 col-span-1">
      {user.PHONE1 && user.PHONE1 !== "." ? user.PHONE1 : "-"}
    </TableCell>
    <TableCell className="border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 col-span-1">
      {user.CR_LIMIT ? `₹${user.CR_LIMIT.toLocaleString()}` : "-"}
    </TableCell>
  </TableRow>
);

// Loading Row Component
const LoadingRow = () => (
  <TableRow className="grid grid-cols-7 py-1 items-center md:grid-cols-12 md:mx-3">
    {Array.from({ length: 7 }).map((_, index) => (
      <TableCell
        key={index}
        className={`border-0 pt-0 pr-[24px] pb-0 pl-[9px] text-[12px] leading-4 ${
          index === 1 ? "md:col-span-3 col-span-2" : "col-span-1"
        }`}
      >
        <Skeleton />
      </TableCell>
    ))}
  </TableRow>
);

// Main Component
const OwnerUsers = () => {
  const classes = useStyles();
  const { globalContext, dispatch } = useContext(GlobalContext);
  const params = useParams();
  const history = useHistory();

  // State
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [isLoadingExportData, setIsLoadingExportData] = useState(false);

  // API calls
  const { data: usersData, error: usersError } = useSWR(
    `/users?mobileNo=${params.mobileNo}&per_page=${rowsPerPage}&page=${page}`
  );

  const { data: exportUsersData } = useSWR(
    `/users?mobileNo=${params.mobileNo}&export=true`
  );

  // Event handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setIsLoading(true);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setIsLoading(true);
  };

  const handleSearch = () => {
    setIsLoading(true);
    if (search.trim()) {
      history.push(`/${params.mobileNo}/owner/users?type=search`);
      setUsers([]);
      Instance.get(`/users?mobileNo=${params.mobileNo}&query=${search}`)
        .then((res) => {
          if (res.status === 200) {
            setIsLoading(false);
            setHasNextPage(false);
            setUsers(res.data.result);
          }
        })
        .catch((e) => {
          setIsLoading(false);
          dispatch({
            type: SHOW_SNACKBAR,
            payload: {
              message: e.response?.data?.message || "Something went wrong",
              type: "error",
            },
          });
        });
    } else {
      history.push(`/${params.mobileNo}/owner/users`);
      setPage(0);
    }
  };

  const exportPdf = () => {
    const doc = new jsPDF("landscape", "pt", "A4");
    const marginLeft = 40;

    doc.setFontSize(15);
    const title = "Users";

    const headers = [
      ["Code", "User Name", "Group", "Area", "City", "Phone", "Credit Limit"],
    ];

    const data = exportData.map((user) => [
      user.USER_CD,
      user.USER_NAME,
      user.GROUP_CD || "-",
      user.AREA || "-",
      user.CITY || "-",
      user.PHONE1 && user.PHONE1 !== "." ? user.PHONE1 : "-",
      user.CR_LIMIT ? `₹${user.CR_LIMIT.toLocaleString()}` : "-",
    ]);

    const content = {
      startY: 70,
      head: headers,
      body: data,
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 150 },
        2: { cellWidth: 60 },
        3: { cellWidth: 100 },
        4: { cellWidth: 100 },
        5: { cellWidth: 100 },
        6: { cellWidth: 100 },
      },
    };

    const width = doc.internal.pageSize.getWidth();
    doc.setFontSize(14).setFont(undefined, "bold");
    doc.text(
      globalContext.company?.[0]?.COMP_NAME || "Company Name",
      width / 2,
      20,
      {
        align: "center",
      }
    );
    doc.setFontSize(8);
    doc.text(
      globalContext.company?.[0]?.ADDRS || "Company Address",
      width / 2,
      38,
      {
        align: "center",
      }
    );
    doc.setFontSize(18);
    doc.text(title, marginLeft, 60);
    doc.autoTable(content);
    doc.save("Users.pdf");
  };

  // Effects
  useEffect(() => {
    if (exportUsersData) {
      setExportData(exportUsersData.data.result);
      setIsLoadingExportData(false);
    }
  }, [exportUsersData]);

  useEffect(() => {
    if (usersData) {
      setIsLoading(false);
      setHasNextPage(!!usersData.data.pagination.nextPage);
      setUsers(usersData.data.result);
    }
  }, [usersData]);

  useEffect(() => {
    if (usersError) {
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Failed to load users",
          type: "error",
        },
      });
    }
  }, [usersError, dispatch]);

  return (
    <div>
      <OwnerHeader>
        <div className="md:flex items-center justify-between px-3 md:px-4 py-3">
          <div className="flex justify-between">
            <div>
              <div className="text-indigo-500 font-semibold text-sm md:text-base">
                Store Management
              </div>
              <div className="text-xl md:text-2xl font-extrabold leading-7 sm:leading-10 truncate mt-1">
                Users
              </div>
            </div>
            {window.innerWidth < 600 && (
              <div className="mr-1">
                {isLoadingExportData ? (
                  <CircularProgress size={30} />
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      className="text-blue-600 border-[#3333cc] rounded-full mt-2 md:mt-0"
                      startIcon={<SaveAltIcon />}
                    >
                      <CSVLink
                        data={exportData}
                        headers={csvHeaders}
                        filename="UserSettings.csv"
                        className="text-blue-600"
                      >
                        Excel
                      </CSVLink>
                    </Button>
                    <Button
                      variant="outlined"
                      className="text-blue-600 border-[#3333cc] rounded-full mt-2 md:mt-0 ml-2"
                      startIcon={<InsertDriveFileOutlinedIcon />}
                      onClick={exportPdf}
                    >
                      PDF
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="md:flex">
            {window.innerWidth > 600 && (
              <div className="mr-2">
                {isLoadingExportData ? (
                  <CircularProgress size={30} />
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      className="text-blue-600 border-[#3333cc] rounded-full mt-2 md:mt-0"
                      startIcon={<SaveAltIcon />}
                    >
                      <CSVLink
                        data={exportData}
                        headers={csvHeaders}
                        filename="UserSettings.csv"
                        className="text-blue-600"
                      >
                        Excel
                      </CSVLink>
                    </Button>
                    <Button
                      variant="outlined"
                      className="text-blue-600 border-[#3333cc] rounded-full mt-2 md:mt-0 ml-2"
                      startIcon={<InsertDriveFileOutlinedIcon />}
                      onClick={exportPdf}
                    >
                      PDF
                    </Button>
                  </>
                )}
              </div>
            )}
            <div className="flex items-center mb-2 mt-2 md:mt-0">
              <Input
                placeholder="Search users..."
                className="bg-white border-[1px] border-[#3333cc] px-2 py-1 rounded-lg"
                disableUnderline
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
              <Button
                variant="contained"
                disableElevation
                className="ml-2 bg-[#3333cc] text-white"
                onClick={handleSearch}
              >
                Search
              </Button>
            </div>
          </div>
        </div>
        <Container
          maxWidth="xl"
          className="pt-2 bg-[#F1F5F9] min-h-screen pb-7 px-1"
        >
          <div className="bg-white shadow-sm rounded p-2">
            <div className="max-h-screen overflow-x-hidden pr-1">
              <TableContainer style={{ overflowX: "clip" }} component={Paper}>
                <div className="max-h-screen overflow-x-hidden">
                  <Table stickyHeader>
                    <EnhancedTableHead
                      classes={classes}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {isLoading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <LoadingRow key={index} />
                        ))
                      ) : !isLoading && users.length === 0 ? (
                        <TableRow hover tabIndex={-1}>
                          <TableCell align="center" colSpan={7}>
                            No users found
                          </TableCell>
                        </TableRow>
                      ) : (
                        stableSort(users, getComparator(order, orderBy)).map(
                          (user) => (
                            <UserTableRow key={user.USER_CD} user={user} />
                          )
                        )
                      )}
                    </TableBody>
                    <TableFooter>
                      <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component={Paper}
                        count={usersData?.data.pagination.total || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={(props) => (
                          <TablePaginationActions
                            {...props}
                            hasNextPage={hasNextPage}
                          />
                        )}
                        elevation={0}
                      />
                    </TableFooter>
                  </Table>
                </div>
              </TableContainer>
            </div>
          </div>
        </Container>
      </OwnerHeader>
    </div>
  );
};

export default OwnerUsers;
